/**
 * VoD Upload Modal
 * Upload video links with player/team/event associations
 * 📹 Peck Pirates Video Library
 */

import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, addDoc, serverTimestamp, query, orderBy, limit } from 'firebase/firestore';
import type { EventDocument, ScoutingReportDocument } from '@schemas/firestore';

// Game options
const GAMES = [
    { id: 'smash-bros-solo', name: 'Smash Bros Solo' },
    { id: 'smash-bros-crews', name: 'Smash Bros Crews' },
    { id: 'rocket-league', name: 'Rocket League' },
    { id: 'mario-kart', name: 'Mario Kart' },
    { id: 'madden', name: 'Madden NFL' },
    { id: 'fortnite', name: 'Fortnite' },
];

// Association type
type AssociationType = 'event' | 'opponent' | 'team' | 'general';

interface VodUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
    userName: string;
    defaultEventId?: string;
    defaultEventTitle?: string;
    onSuccess?: () => void;
}

interface EventOption {
    id: string;
    title: string;
    date: string;
}

interface OpponentOption {
    name: string;
    team?: string;
}

export default function VodUploadModal({
    isOpen,
    onClose,
    userId,
    userName,
    defaultEventId,
    // Unused but could be used to auto-fill title
    defaultEventTitle: _defaultEventTitle,
    onSuccess,
}: VodUploadModalProps) {
    // Form state
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [game, setGame] = useState(GAMES[0].id);
    const [associationType, setAssociationType] = useState<AssociationType>(
        defaultEventId ? 'event' : 'general'
    );
    const [notes, setNotes] = useState('');

    // Association-specific state
    const [selectedEventId, setSelectedEventId] = useState(defaultEventId || '');
    const [opponentName, setOpponentName] = useState('');
    const [opponentTeam, setOpponentTeam] = useState('');
    const [teamName, setTeamName] = useState('Peck Pirates');

    // Options for dropdowns
    const [events, setEvents] = useState<EventOption[]>([]);
    const [opponents, setOpponents] = useState<OpponentOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Load events and opponents for association
    useEffect(() => {
        async function loadOptions() {
            if (!isOpen) return;

            try {
                // Load recent events
                const eventsSnap = await getDocs(
                    query(collection(db, 'events'), orderBy('date', 'desc'), limit(20))
                );
                const eventsData = eventsSnap.docs.map(doc => ({
                    id: doc.id,
                    title: (doc.data() as EventDocument).title,
                    date: (doc.data() as EventDocument).date,
                }));
                setEvents(eventsData);

                // Load opponents from scouting reports
                const reportsSnap = await getDocs(
                    query(collection(db, 'scoutingReports'), orderBy('createdAt', 'desc'), limit(50))
                );
                const opponentMap = new Map<string, OpponentOption>();
                reportsSnap.docs.forEach(doc => {
                    const data = doc.data() as ScoutingReportDocument;
                    opponentMap.set(data.opponentName, {
                        name: data.opponentName,
                        team: data.opponentTeam,
                    });
                });
                setOpponents(Array.from(opponentMap.values()));

            } catch (err) {
                console.error('Error loading options:', err);
            } finally {
                setLoading(false);
            }
        }

        loadOptions();
    }, [isOpen]);

    // Reset form when opened
    useEffect(() => {
        if (isOpen) {
            if (defaultEventId) {
                setSelectedEventId(defaultEventId);
                setAssociationType('event');
            }
        }
    }, [isOpen, defaultEventId]);

    // Validate YouTube/Twitch URL
    const isValidUrl = (urlString: string): boolean => {
        try {
            const urlObj = new URL(urlString);
            return urlObj.hostname.includes('youtube') ||
                urlObj.hostname.includes('youtu.be') ||
                urlObj.hostname.includes('twitch') ||
                urlObj.hostname.includes('vimeo');
        } catch {
            return false;
        }
    };

    // Handle submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!title.trim() || !url.trim()) {
            setError('Title and URL are required');
            return;
        }

        if (!isValidUrl(url)) {
            setError('Please enter a valid YouTube, Twitch, or Vimeo URL');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            // Build VoD document
            const vodData: Record<string, unknown> = {
                title: title.trim(),
                url: url.trim(),
                game,
                notes: notes.trim() || null,
                associationType,
                uploadedBy: userId,
                uploadedByName: userName,
                createdAt: serverTimestamp(),
            };

            // Add association-specific fields
            switch (associationType) {
                case 'event':
                    vodData.eventId = selectedEventId;
                    const selectedEvent = events.find(e => e.id === selectedEventId);
                    vodData.eventTitle = selectedEvent?.title || null;
                    vodData.eventDate = selectedEvent?.date || null;
                    break;
                case 'opponent':
                    vodData.opponentName = opponentName.trim();
                    vodData.opponentTeam = opponentTeam.trim() || null;
                    break;
                case 'team':
                    vodData.teamName = teamName.trim();
                    break;
                case 'general':
                    // No additional fields
                    break;
            }

            await addDoc(collection(db, 'eventVods'), vodData);

            // Reset form
            setTitle('');
            setUrl('');
            setNotes('');
            setOpponentName('');
            setOpponentTeam('');

            onSuccess?.();
            onClose();
        } catch (err) {
            console.error('Error uploading VoD:', err);
            setError('Failed to upload VoD. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-secondary-900 rounded-xl border border-secondary-700 shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-purple-900/60 via-secondary-900 to-purple-900/60 border-b border-purple-500/30 p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">📹</span>
                            <div>
                                <h2 className="text-lg font-bold text-white">Upload VoD</h2>
                                <p className="text-xs text-purple-400 uppercase tracking-wider">
                                    Add Video Recording
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-secondary-400 hover:text-white hover:bg-secondary-800 rounded-lg transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {error && (
                        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-secondary-300 mb-1">
                            Video Title *
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., Crews Match vs Kettering Round 1"
                            className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-purple-500"
                            required
                        />
                    </div>

                    {/* URL */}
                    <div>
                        <label className="block text-sm font-medium text-secondary-300 mb-1">
                            Video URL *
                        </label>
                        <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://youtube.com/watch?v=..."
                            className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-purple-500"
                            required
                        />
                        <p className="text-xs text-secondary-500 mt-1">
                            YouTube, Twitch, or Vimeo links accepted
                        </p>
                    </div>

                    {/* Game */}
                    <div>
                        <label className="block text-sm font-medium text-secondary-300 mb-1">
                            Game
                        </label>
                        <select
                            value={game}
                            onChange={(e) => setGame(e.target.value)}
                            className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                        >
                            {GAMES.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Association Type */}
                    <div>
                        <label className="block text-sm font-medium text-secondary-300 mb-2">
                            Associate With
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { id: 'event', label: '📅 Event', desc: 'Link to match/practice' },
                                { id: 'opponent', label: '🎯 Opponent', desc: 'For scouting' },
                                { id: 'team', label: '👥 Our Team', desc: 'Team highlights' },
                                { id: 'general', label: '📚 General', desc: 'Training/tutorials' },
                            ].map(opt => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => setAssociationType(opt.id as AssociationType)}
                                    className={`p-3 rounded-lg border text-left transition-colors ${associationType === opt.id
                                        ? 'bg-purple-500/20 border-purple-500 text-white'
                                        : 'bg-secondary-800 border-secondary-600 text-secondary-300 hover:border-secondary-500'
                                        }`}
                                >
                                    <p className="font-medium text-sm">{opt.label}</p>
                                    <p className="text-xs text-secondary-400">{opt.desc}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Association-specific fields */}
                    {associationType === 'event' && (
                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-1">
                                Select Event
                            </label>
                            {loading ? (
                                <div className="p-3 bg-secondary-800 rounded-lg text-secondary-400 text-sm">
                                    Loading events...
                                </div>
                            ) : (
                                <select
                                    value={selectedEventId}
                                    onChange={(e) => setSelectedEventId(e.target.value)}
                                    className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                >
                                    <option value="">Select an event...</option>
                                    {events.map(event => (
                                        <option key={event.id} value={event.id}>
                                            {event.date} - {event.title}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {associationType === 'opponent' && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-secondary-300 mb-1">
                                    Opponent Name
                                </label>
                                <input
                                    type="text"
                                    value={opponentName}
                                    onChange={(e) => setOpponentName(e.target.value)}
                                    placeholder="Player or team name"
                                    list="opponent-suggestions"
                                    className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-purple-500"
                                />
                                <datalist id="opponent-suggestions">
                                    {opponents.map((opp, i) => (
                                        <option key={i} value={opp.name}>
                                            {opp.team && `(${opp.team})`}
                                        </option>
                                    ))}
                                </datalist>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-secondary-300 mb-1">
                                    Opponent Team/School
                                </label>
                                <input
                                    type="text"
                                    value={opponentTeam}
                                    onChange={(e) => setOpponentTeam(e.target.value)}
                                    placeholder="Optional"
                                    className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-purple-500"
                                />
                            </div>
                            <p className="text-xs text-yellow-400">
                                💡 This VoD will appear when searching intel on this opponent
                            </p>
                        </div>
                    )}

                    {associationType === 'team' && (
                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-1">
                                Team
                            </label>
                            <input
                                type="text"
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                            />
                        </div>
                    )}

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-secondary-300 mb-1">
                            Notes
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Optional notes about this video..."
                            rows={2}
                            className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-purple-500 resize-none"
                        />
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-4 border-t border-secondary-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 bg-secondary-800 text-secondary-300 rounded-lg hover:bg-secondary-700 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 px-4 py-2.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                            {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Uploading...
                                </span>
                            ) : (
                                '📹 Upload VoD'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
